import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch, clearCsrfToken } from '../api';
import { getTelegramInitData } from '../telegram';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  /** Реальный залогиненный пользователь (для суперюзера — отдельно от эффективного). */
  const [actor, setActor] = useState(null);
  const [impersonationUsers, setImpersonationUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [telegramAuthError, setTelegramAuthError] = useState('');

  const loadImpersonationUsers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/auth/users/');
      if (!res.ok) return;
      const data = await res.json();
      setImpersonationUsers(Array.isArray(data.users) ? data.users : []);
    } catch {
      setImpersonationUsers([]);
    }
  }, []);

  const applyMePayload = useCallback((data) => {
    if (data.user) setUser(data.user);
    if (data.actor) setActor(data.actor);
  }, []);

  const applyAuthPayload = useCallback((data) => {
    applyMePayload(data);
    if (process.env.REACT_APP_ANALYSIS_TOOLS === "true" && data.actor?.is_superuser) {
      loadImpersonationUsers();
    } else {
      setImpersonationUsers([]);
    }
    return data.user;
  }, [applyMePayload, loadImpersonationUsers]);

  const loginWithTelegram = useCallback(async () => {
    const initData = getTelegramInitData();
    if (!initData) {
      return null;
    }

    const res = await fetch('/api/auth/telegram/', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ initData }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Не удалось войти через Telegram');
    }

    const data = await res.json();
    setTelegramAuthError('');
    return applyAuthPayload(data);
  }, [applyAuthPayload]);

  const checkAuth = useCallback(async () => {
    try {
      const res = await apiFetch('/api/auth/me/');
      if (res.ok) {
        const data = await res.json();
        setTelegramAuthError('');
        return applyAuthPayload(data);
      }
      try {
        const telegramUser = await loginWithTelegram();
        if (telegramUser) return telegramUser;
      } catch (err) {
        setTelegramAuthError(err.message || 'Не удалось войти через Telegram');
      }
      setUser(null);
      setActor(null);
      setImpersonationUsers([]);
      return null;
    } catch {
      setUser(null);
      setActor(null);
      setImpersonationUsers([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyAuthPayload, loginWithTelegram]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    const res = await apiFetch('/api/auth/login/', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Ошибка входа');
    }
    const data = await res.json();
    return applyAuthPayload(data);
  };

  const setActAsUser = async (userId) => {
    const res = await apiFetch('/api/auth/act-as/', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Не удалось сменить пользователя');
    }
    const data = await res.json();
    applyMePayload(data);
    return data.user;
  };

  const logout = async () => {
    try {
      await apiFetch('/api/auth/logout/', {
        method: 'POST',
      });
    } finally {
      clearCsrfToken();
      setUser(null);
      setActor(null);
      setImpersonationUsers([]);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        actor,
        loading,
        telegramAuthError,
        login,
        loginWithTelegram,
        logout,
        checkAuth,
        impersonationUsers,
        loadImpersonationUsers,
        setActAsUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
